import { parseDbTimestamp } from "@repo/shared";
import type { WebhookDelivery } from "@repo/shared/types";
import { enGB, nb } from "date-fns/locale";
import { AlertCircle, CheckCircle2, ChevronRight, Clock, Loader2, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { StatusBadge } from "@/components/ui/status-badge";
import { getWebhookDeliveries } from "@/lib/api/webhooks";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { distanceToNowFromDb } from "@/lib/utils";
import { T } from "./webhook-logs-dialog.i18n";

const getDateLocale = (lang: string) => {
  return lang === "nb_NO" ? nb : enGB;
};

type WebhookLogsDialogProps = {
  open: boolean;
  webhookId: string | null;
  webhookName: string;
  onClose: () => void;
};

export function WebhookLogsDialog({
  open,
  webhookId,
  webhookName,
  onClose,
}: WebhookLogsDialogProps) {
  const { userLanguage } = useUserLanguage();
  const { formatDateTime } = useRegionalFormat();
  const t = useT(T);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<WebhookDelivery | null>(null);

  const loadDeliveries = useCallback(async () => {
    if (!webhookId) return;

    setIsLoading(true);
    try {
      const data = await getWebhookDeliveries(webhookId);
      setDeliveries(data);
    } catch (err) {
      log.error({ err, webhookId }, "failed to load webhook deliveries");
    } finally {
      setIsLoading(false);
    }
  }, [webhookId]);

  useEffect(() => {
    if (open && webhookId) {
      loadDeliveries();
    }
  }, [open, webhookId, loadDeliveries]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "pending":
        return <Clock className="h-4 w-4 text-warning" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const labels: Record<string, string> = {
      success: t.success,
      failed: t.failed,
      pending: t.pending,
    };
    return <StatusBadge status={status}>{labels[status] ?? status}</StatusBadge>;
  };

  const formatJson = (jsonString: string | null) => {
    if (!jsonString) return t.notAvailable;
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t.webhookDeliveryLogs}</DialogTitle>
          <DialogDescription>
            {t.recentDeliveriesFor} <strong>{webhookName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex gap-4">
          {/* Left side - Delivery list */}
          <div className="w-1/2 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium">
                {t.recentDeliveries} ({deliveries.length})
              </h3>
              <Button
                data-dialog-guard-ignore
                variant="outline"
                size="sm"
                onClick={loadDeliveries}
                loading={isLoading}
              >
                {t.refresh}
              </Button>
            </div>

            <ScrollArea className="flex-1 border rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : deliveries.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  {t.noDeliveriesYet}
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {deliveries.map((delivery) => (
                    <button
                      type="button"
                      key={delivery.id}
                      onClick={() => setSelectedDelivery(delivery)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedDelivery?.id === delivery.id
                          ? "bg-accent border-primary"
                          : "hover:bg-accent border-transparent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {getStatusIcon(delivery.status)}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">
                                {delivery.eventType}
                              </span>
                              {delivery.isTest && (
                                <Badge variant="outline" className="text-xs px-1 py-0">
                                  {t.test}
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {distanceToNowFromDb(delivery.createdAt, {
                                addSuffix: true,
                                locale: getDateLocale(userLanguage),
                              })}
                              {delivery.duration && <span>• {delivery.duration}ms</span>}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        {getStatusBadge(delivery.status)}
                        {delivery.statusCode && (
                          <Badge variant="outline" className="text-xs">
                            {delivery.statusCode}
                          </Badge>
                        )}
                        {delivery.status === "pending" && (
                          <span className="text-xs text-muted-foreground">
                            {t.attempt} {delivery.attempts}/{delivery.maxAttempts}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Right side - Delivery details */}
          <div className="w-1/2 flex flex-col">
            <h3 className="text-sm font-medium mb-3">{t.deliveryDetails}</h3>
            <div className="flex-1 border rounded-lg bg-background overflow-auto">
              {selectedDelivery ? (
                <div className="p-4 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium">{t.status}</h4>
                      {getStatusBadge(selectedDelivery.status)}
                    </div>
                    {selectedDelivery.errorMessage && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded p-3 mt-2">
                        <p className="text-sm text-destructive">{selectedDelivery.errorMessage}</p>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.eventType}</span>
                      <span className="font-medium">{selectedDelivery.eventType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.eventId}</span>
                      <span className="font-mono text-xs">{selectedDelivery.eventId}</span>
                    </div>
                    {/* URL is snapshotted per delivery — after a URL edit, older
                        deliveries show the endpoint they actually hit. */}
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground">{t.url}</span>
                      <span className="font-mono text-xs break-all text-right">
                        {selectedDelivery.url}
                      </span>
                    </div>
                    {selectedDelivery.statusCode && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t.httpStatus}</span>
                        <span className="font-medium">{selectedDelivery.statusCode}</span>
                      </div>
                    )}
                    {selectedDelivery.duration && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t.duration}</span>
                        <span className="font-medium">{selectedDelivery.duration}ms</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.attempts}</span>
                      <span className="font-medium">
                        {selectedDelivery.attempts} / {selectedDelivery.maxAttempts}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.created}</span>
                      <span className="text-xs">
                        {formatDateTime(parseDbTimestamp(selectedDelivery.createdAt))}
                      </span>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-sm font-medium mb-2">{t.requestPayload}</h4>
                    <div className="bg-muted p-3 rounded max-h-64 overflow-auto">
                      <pre className="text-xs whitespace-pre">
                        {formatJson(selectedDelivery.requestBody)}
                      </pre>
                    </div>
                  </div>

                  {selectedDelivery.responseBody && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-medium mb-2">{t.responseBody}</h4>
                        <div className="bg-muted p-3 rounded max-h-64 overflow-auto">
                          <pre className="text-xs whitespace-pre">
                            {formatJson(selectedDelivery.responseBody)}
                          </pre>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
                  {t.selectDeliveryToView}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
